import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Divider, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AlignHorizontalLeftIcon from 'mdi-react/AlignHorizontalLeftIcon';
import AlignHorizontalCenterIcon from 'mdi-react/AlignHorizontalCenterIcon';
import AlignHorizontalRightIcon from 'mdi-react/AlignHorizontalRightIcon';
import AlignVerticalTopIcon from 'mdi-react/AlignVerticalTopIcon';
import AlignVerticalCenterIcon from 'mdi-react/AlignVerticalCenterIcon';
import AlignVerticalBottomIcon from 'mdi-react/AlignVerticalBottomIcon';
import ArrowSplitVerticalIcon from 'mdi-react/ArrowSplitVerticalIcon';
import ArrowSplitHorizontalIcon from 'mdi-react/ArrowSplitHorizontalIcon';
import ContentDuplicateIcon from 'mdi-react/ContentDuplicateIcon';
import GridIcon from 'mdi-react/GridIcon';
import { sectionHeadingSx } from '../utils/editor-theme';
import type { AlignEdge, DistributeAxis } from '../utils/arrangement';
import type { DrawTarget } from '../models/editor-tool';

interface Props {
  readonly selectedCount: number;
  /** How many of the selection are drawn areas, which are the only things a layer move applies to. */
  readonly areaCount: number;
  /**
   * Whether lining things up applies to this selection at all.
   *
   * False once a desk or a desk image is in it. Desks are placed by the desk tools, spaced by
   * the group panel and nudged by hand; an edge-alignment run across them is a floor-wide move
   * of bookable seats behind one icon, and the spacing it produces is not the spacing any of
   * those tools would have given.
   */
  readonly alignable: boolean;
  readonly onAlign: (edge: AlignEdge) => void;
  readonly onDistribute: (axis: DistributeAxis) => void;
  readonly onDuplicate: () => void;
  readonly onArray: () => void;
  readonly onMoveToLayer: (target: DrawTarget) => void;
}

const ALIGNMENTS: ReadonlyArray<{
  readonly edge: AlignEdge;
  readonly labelKey: string;
  readonly Icon: typeof AlignHorizontalLeftIcon;
}> = [
  { edge: 'left', labelKey: 'floorplan-editor-align-left', Icon: AlignHorizontalLeftIcon },
  { edge: 'centreX', labelKey: 'floorplan-editor-align-centre-x', Icon: AlignHorizontalCenterIcon },
  { edge: 'right', labelKey: 'floorplan-editor-align-right', Icon: AlignHorizontalRightIcon },
  { edge: 'top', labelKey: 'floorplan-editor-align-top', Icon: AlignVerticalTopIcon },
  { edge: 'centreY', labelKey: 'floorplan-editor-align-centre-y', Icon: AlignVerticalCenterIcon },
  { edge: 'bottom', labelKey: 'floorplan-editor-align-bottom', Icon: AlignVerticalBottomIcon },
];

const LAYERS: ReadonlyArray<{ readonly value: DrawTarget; readonly labelKey: string }> = [
  { value: 'room', labelKey: 'floorplan-editor-layer-rooms' },
  { value: 'floor', labelKey: 'floorplan-editor-layer-floor' },
  { value: 'border', labelKey: 'floorplan-editor-layer-border' },
];

/**
 * What can be done to several things at once.
 *
 * Shown only for a multiple selection, because every action here is meaningless on one
 * feature: aligning one thing to itself does nothing, and distributing needs three. Duplicate
 * and duplicate-into-a-grid are the exceptions and stay available throughout — they are the
 * bulk actions that make sense for a single desk, and the two that remain for desks at all.
 */
const BulkActions = ({
  selectedCount,
  areaCount,
  alignable,
  onAlign,
  onDistribute,
  onDuplicate,
  onArray,
  onMoveToLayer,
}: Props): React.JSX.Element | null => {
  const { t } = useTranslation();

  if (selectedCount === 0) {
    return null;
  }

  const canAlign = selectedCount >= 2;
  // Three is the fewest with a middle to move; with two there is nothing between the ends.
  const canDistribute = selectedCount >= 3;

  const iconButton = (
    labelKey: string,
    Icon: typeof AlignHorizontalLeftIcon,
    disabled: boolean,
    onClick: () => void,
  ): React.JSX.Element => (
    <Tooltip key={labelKey} title={String(t(labelKey))}>
      {/* Wrapped so a disabled control keeps its tooltip: MUI drops pointer events on one, and
          "why can I not align this?" is exactly the question a single selection raises. */}
      <span>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={onClick}
          aria-label={String(t(labelKey))}
          sx={{ minWidth: 36, px: 0.5 }}
        >
          <Icon size={18} />
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      <Typography sx={sectionHeadingSx}>{t('floorplan-editor-bulk-actions')}</Typography>

      {/* Gone rather than disabled once a desk is selected. A disabled row invites the question
          of what would enable it, and the answer — select something that is not a desk — is not
          a thing anyone was trying to do. */}
      {alignable && (
        <>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ALIGNMENTS.map(({ edge, labelKey, Icon }) => iconButton(
              labelKey,
              Icon,
              !canAlign,
              () => onAlign(edge),
            ))}
          </Stack>

          <Stack direction="row" spacing={0.5}>
            {iconButton(
              'floorplan-editor-distribute-x',
              ArrowSplitVerticalIcon,
              !canDistribute,
              () => onDistribute('x'),
            )}
            {iconButton(
              'floorplan-editor-distribute-y',
              ArrowSplitHorizontalIcon,
              !canDistribute,
              () => onDistribute('y'),
            )}
          </Stack>
        </>
      )}

      <Stack direction="row" spacing={0.5}>
        {iconButton('floorplan-editor-duplicate', ContentDuplicateIcon, false, onDuplicate)}
        {iconButton('floorplan-editor-array', GridIcon, false, onArray)}
      </Stack>

      {areaCount > 0 && (
        <>
          <Divider />
          <TextField
            select
            size="small"
            value=""
            onChange={(event): void => onMoveToLayer(event.target.value as DrawTarget)}
            label={String(t('floorplan-editor-move-to-layer', { count: areaCount }))}
            // Deliberately never holds a value. The selection can span three layers, so there
            // is no single current one to show — this is an action, worded as one.
            helperText={String(t('floorplan-editor-move-to-layer-hint'))}
          >
            {LAYERS.map(({ value, labelKey }) => (
              <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
            ))}
          </TextField>
        </>
      )}
    </Stack>
  );
};

export default BulkActions;
